"use client";

import { cn } from "@ui/lib";
import { PauseIcon, PlayIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { track } from "../analytics";
import { Chevron, mkLink } from "../components/MkButton";

interface Line {
	at: number;
	who: "Caller" | "Agent";
	text: string;
	/** How the Arabic sounds, in Latin letters. */
	said?: string;
}

const CALLS: { id: string; label: string; src: string; lines: Line[] }[] = [
	{
		id: "saudi",
		label: "Saudi Arabic",
		src: "/marketing/audio/saudi-call.mp3",
		lines: [
			{
				at: 0,
				who: "Caller",
				text: "Hi, I want five kilos of basmati rice.",
				said: "Assalamu alaykum, abgha rizz basmati khamsa kilo.",
			},
			{
				at: 3.67,
				who: "Agent",
				text: "Of course. We have Abu Kass and Taj Al Hind. Shall I add one of them?",
				said: "Wa alaykum assalam, abshir. 3indna Abu Kass w Taj Al Hind. Tabi adeef lak wa7id minhum?",
			},
			{
				at: 9.65,
				who: "Caller",
				text: "Abu Kass, and add a kilo of chicken with it.",
				said: "Abu Kass, w deef ma3ah dajaj kilo.",
			},
			{
				at: 12.72,
				who: "Agent",
				text: "Done, Abu Kass and a kilo of chicken are in your basket. Anything else?",
				said: "Tamm, adaft Abu Kass w kilo dajaj lil salla. Tabi shay thani?",
			},
		],
	},
	{
		id: "english",
		label: "English",
		src: "/marketing/audio/english-call.mp3",
		lines: [
			{
				at: 0,
				who: "Caller",
				text: "Hi, I need milk, bread and eggs for tomorrow morning.",
			},
			{
				at: 4.22,
				who: "Agent",
				text: "Sure. I found Almarai milk, Al Forno pita and fifteen white eggs. Shall I add all three?",
			},
			{ at: 11.39, who: "Caller", text: "Yes please, and some hummus." },
			{
				at: 13.9,
				who: "Agent",
				text: "Done. All four are in your basket. Anything else?",
			},
		],
	},
];

/** The player's waveform: fixed, pleasant peaks with ids, filled as the call plays. */
const PEAKS = Array.from({ length: 56 }, (_, i) => ({
	id: `peak-${i}`,
	// Whole percentages: the server and the browser print long floats differently.
	height: Math.round(
		25 + 75 * Math.abs(Math.sin(i * 0.9) * Math.cos(i * 0.31)),
	),
}));

const FACTS = [
	{ value: "<1s", label: "to a spoken answer" },
	{ value: "30+", label: "languages and dialects" },
	{ value: "24/7", label: "every call answered" },
];

/** The dark moment of the page: hear the assistant take an order by phone. */
export function Voice() {
	return (
		<section
			id="voice"
			aria-labelledby="voice-title"
			className="mk-section bg-(--mk-night) text-(--mk-on-night)"
		>
			<div className="mk-container">
				<div className="mk-reveal mx-auto max-w-3xl text-center">
					<h2 id="voice-title" className="mk-h2">
						It answers the phone, too.
					</h2>
					<p className="mk-lead mt-5 text-(--mk-on-night-muted)">
						Shoppers call and order by voice, in their own dialect.
						It hears them as they speak, searches the same catalog
						and reads the basket back. The order is placed only when
						they confirm on the keypad.
					</p>
				</div>
				<div className="mk-rise mx-auto mt-14 max-w-3xl sm:mt-16">
					<CallPlayer />
				</div>
				<dl className="mx-auto mt-14 grid max-w-3xl grid-cols-3 gap-4 text-center sm:mt-16">
					{FACTS.map((fact) => (
						<div
							key={fact.label}
							className="flex flex-col items-center"
						>
							<dt className="order-last mt-2 text-(--mk-on-night-muted) text-[13px] leading-snug sm:text-[15px]">
								{fact.label}
							</dt>
							<dd className="font-semibold text-[clamp(2rem,4.5vw,3rem)] leading-none tracking-[-0.04em]">
								{fact.value}
							</dd>
						</div>
					))}
				</dl>
				<p className="mt-12 text-center">
					<a
						href="/channels/voice"
						className={mkLink(
							"text-(--mk-signal-night) text-[17px]",
						)}
					>
						How voice ordering works
						<Chevron />
					</a>
				</p>
			</div>
		</section>
	);
}

/** A sample call you can play: a language switch, the waveform, and the line being said, large. */
export function CallPlayer() {
	const audio = useRef<HTMLAudioElement>(null);
	const [callIndex, setCallIndex] = useState(0);
	const [playing, setPlaying] = useState(false);
	const [time, setTime] = useState(0);
	const [duration, setDuration] = useState(17.3);
	const call = CALLS[callIndex] ?? CALLS[0];

	useEffect(() => {
		const el = audio.current;
		if (!el) {
			return;
		}
		let raf = 0;
		const loop = () => {
			setTime(el.currentTime);
			raf = requestAnimationFrame(loop);
		};
		const onPlay = () => {
			setPlaying(true);
			raf = requestAnimationFrame(loop);
		};
		const onPause = () => {
			setPlaying(false);
			cancelAnimationFrame(raf);
		};
		const onMeta = () =>
			Number.isFinite(el.duration) && setDuration(el.duration);
		el.addEventListener("play", onPlay);
		el.addEventListener("pause", onPause);
		el.addEventListener("ended", onPause);
		el.addEventListener("loadedmetadata", onMeta);
		return () => {
			cancelAnimationFrame(raf);
			el.removeEventListener("play", onPlay);
			el.removeEventListener("pause", onPause);
			el.removeEventListener("ended", onPause);
			el.removeEventListener("loadedmetadata", onMeta);
		};
	}, []);

	if (!call) {
		return null;
	}
	const started = playing || time > 0;
	const current =
		[...call.lines].reverse().find((line) => time >= line.at - 0.05) ??
		call.lines[0];
	const progress = Math.min(1, time / duration);

	const choose = (i: number) => {
		audio.current?.pause();
		setCallIndex(i);
		setTime(0);
	};
	const toggle = () => {
		const el = audio.current;
		if (!el) {
			return;
		}
		if (el.paused) {
			if (el.currentTime === 0) {
				track("sample_call_played", { language: call.label });
			}
			void el.play();
		} else {
			el.pause();
		}
	};

	return (
		<div className="rounded-[32px] bg-(--mk-night-2) p-6 sm:p-10">
			<div
				role="tablist"
				aria-label="Sample call language"
				className="mx-auto flex w-fit gap-1 rounded-full bg-(--mk-night) p-1"
			>
				{CALLS.map((c, i) => (
					<button
						key={c.id}
						type="button"
						role="tab"
						aria-selected={i === callIndex}
						onClick={() => choose(i)}
						className={cn(
							"rounded-full px-4 py-1.5 text-[14px] outline-none transition-colors duration-(--duration-hover) focus-visible:ring-2 focus-visible:ring-(--mk-signal-night)",
							i === callIndex
								? "bg-(--mk-night-3) text-(--mk-on-night)"
								: "text-(--mk-on-night-muted) hover:text-(--mk-on-night)",
						)}
					>
						{c.label}
					</button>
				))}
			</div>

			<div className="mt-8 flex items-center gap-5">
				<button
					type="button"
					onClick={toggle}
					aria-label={
						playing
							? "Pause the sample call"
							: "Play the sample call"
					}
					className="relative grid size-16 shrink-0 place-items-center rounded-full bg-(--mk-on-night) text-(--mk-night) outline-none transition-transform duration-(--duration-press) active:scale-95 focus-visible:ring-2 focus-visible:ring-(--mk-signal-night) focus-visible:ring-offset-2 focus-visible:ring-offset-(--mk-night-2)"
				>
					{playing ? null : (
						<span className="mk-pulse absolute inset-0 rounded-full bg-(--mk-on-night)" />
					)}
					{playing ? (
						<PauseIcon className="relative size-6" aria-hidden />
					) : (
						<PlayIcon
							className="relative ms-0.5 size-6"
							aria-hidden
						/>
					)}
				</button>
				<div
					className="flex h-14 flex-1 items-center gap-[3px]"
					aria-hidden
				>
					{PEAKS.map((peak, i) => (
						<span
							key={peak.id}
							className={cn(
								"block w-full rounded-full transition-colors duration-150",
								i / PEAKS.length < progress
									? "bg-(--mk-signal-night)"
									: "bg-(--mk-night-3)",
							)}
							style={{ height: `${peak.height}%` }}
						/>
					))}
				</div>
			</div>

			<div
				className="mt-8 min-h-[9.5rem] sm:min-h-[8.5rem]"
				aria-live="polite"
			>
				{current ? (
					<div key={`${call.id}-${current.at}`} className="mk-pop">
						<p className="text-(--mk-on-night-muted) text-[14px]">
							{started
								? current.who === "Agent"
									? "Assistant"
									: "Shopper"
								: "Press play to hear the call"}
						</p>
						<p className="mt-2 font-medium text-[clamp(1.25rem,2.6vw,1.75rem)] leading-snug tracking-[-0.02em]">
							{current.text}
						</p>
						{current.said ? (
							<p className="mt-2 text-(--mk-on-night-muted) text-[14px] italic">
								{current.said}
							</p>
						) : null}
					</div>
				) : null}
			</div>
			<audio ref={audio} src={call.src} preload="none">
				<track
					kind="captions"
					src={call.src.replace(/\.mp3$/, ".vtt")}
					srcLang="en"
					label="English"
					default
				/>
			</audio>
			<p className="mt-4 text-(--mk-on-night-muted) text-[13px]">
				Recorded with the voices the assistant uses on calls. English
				captions.
			</p>
		</div>
	);
}
